import { createContext, memo, useCallback, useState } from 'react';
import { deleteBookmarkLocation, loadBookmarks, saveBookmarkLocation, updateBookmarkLocation } from "utils/firestore";

// values and functions implemented below
export const BookmarksContext = createContext({});


/**
 * Bookmarks context provider
 * Provides context for bookmarks and keeps track of the bookmarks from firestore
 */
export const BookmarksContextProvider = memo((props) => {
    const { children } = props;
    const [isLoading, setIsLoading] = useState(false);
    const [bookmarks, setBookmarks] = useState([]);

    const addBookmark = useCallback((assetId, bookmarkData) => {
        return saveBookmarkLocation(assetId, bookmarkData)
            .then(() => loadAndSetBookmarks(assetId));
    }, []);

    const deleteBookmark = useCallback((assetId, bookmarkData) => {
        return deleteBookmarkLocation(assetId, bookmarkData)
            .then(() => loadAndSetBookmarks(assetId));
    }, []);

    const loadAndSetBookmarks = useCallback((assetId) => {
        setIsLoading(true);
        return loadBookmarks(assetId).then(bookmarks => {
            setBookmarks(bookmarks);
            setIsLoading(false);
            return bookmarks;
        });
    }, []);

    const toggleBookmark = useCallback((assetId, bookmarkData) => {
        const isBookmarked = bookmarks.some(bookmark => bookmark.locations.position === bookmarkData.locations.position);
        if (isBookmarked) {
            return deleteBookmark(assetId, bookmarkData);
        } else {
            return addBookmark(assetId, bookmarkData);
        }
    }, [bookmarks]);

    const isBookmarkActive = useCallback((location) => {
        return bookmarks.some(bookmark => bookmark.locations.position === location.locations.position);
    }, [bookmarks]);

    const updateBookmark = useCallback((assetId, bookmarkData, title, note) => {
        return updateBookmarkLocation(assetId, bookmarkData, title, note)
            .then(() => loadAndSetBookmarks(assetId));
    }, []);

    return (
        <BookmarksContext.Provider value={{
            isLoading: isLoading,
            bookmarks: bookmarks,
            toggleBookmark: toggleBookmark,
            loadBookmarks: loadAndSetBookmarks,
            deleteBookmark: deleteBookmark,
            updateBookmark: updateBookmark,
            isBookmarkActive: isBookmarkActive,
        }}>
            {children}
        </BookmarksContext.Provider>
    )
});
